import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import { useTranslation } from 'react-i18next'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { ReactComponent as DropdownIcon } from 'assets/icons/dropdown.svg'
import { useField } from 'formik'
import { ErrorMessage } from 'components/Shared/ErrorMessage'
import { Loader } from 'components/Shared/Loader'
import { debounce } from '@material-ui/core'
import { isFieldRequired } from 'components/Shared/FormikForm'
import { useValidationSchema } from 'context/validationSchema'
import { AvenirRegular } from '../../styles/variables'

const useStyles = makeStyles({
  defaultWidth: {
    width: '310px'
  },
  stylesWithHelperText: {
    '& .MuiFormHelperText-root': {
      color: 'orange',
      fontFamily: AvenirRegular,
    }
  }
})

function defaultOnChange () {
}

function defaultOptionLabel (option) {
  return option?.name
}

function defaultOptionValue (option) {
  return option?.id
}

const ROWS_PER_PAGE = 50

export function ControlledSelectFieldWithInfinityScroll ({
  initialOption,
  getOptionLabel = defaultOptionLabel,
  getOptionValue = defaultOptionValue,
  onChange = defaultOnChange,
  label,
  fetchData,
  disableClear = false,
  disabled,
  className,
  fieldName,
  inputClassName,
  required,
  addObjectParams,
  searchField,
  helperText,

}) {
  const [, meta, helpers] = useField(fieldName)
  const { error, touched, value } = meta
  const { schema } = useValidationSchema()

  const { setValue, setTouched } = helpers
  const [pageNumber, setPageNumber] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [requestFilters, setRequestFilters] = useState([])
  const [isLastPage, setIsLastPage] = useState(false)
  const [options, setOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(initialOption)

  const internalStyles = useStyles()
  const { t } = useTranslation()

  const isRequired = useMemo(() => isFieldRequired(schema, fieldName), [schema, fieldName])

  const fetch = useCallback(() => fetchData(pageNumber, ROWS_PER_PAGE, requestFilters),
    [fetchData, pageNumber, requestFilters])

  const removeDuplicates = useCallback((items) => {
    return selectedOption
      ? items.filter(i => getOptionValue(i) !== getOptionValue(selectedOption))
      : items
  }, [getOptionValue, selectedOption])

  const fetchMoreData = useCallback(() => {
    setIsLoading(true)
    fetch().then(response => {
        const newOptions = [...options,
          ...removeDuplicates(response.items)]
        setOptions(newOptions)
        setPageNumber(pageNumber + 1)
        setIsLastPage(handleIsLastPageCheck(response))
      }
    ).finally(() => setIsLoading(false))
  }, [options, fetch, pageNumber, removeDuplicates])

  // FIXME missing dependency in array
  // eslint-disable-next-line
  useEffect(fetchMoreData, [])

  useEffect(() => {
    setSelectedOption(initialOption)
  }, [initialOption])

  const onInputChange = useMemo(
    () => debounce((event, searchInput, reason) => {
      if (reason === 'input') {
        setIsLoading(true)
        const newFilter = [{ key: searchField, value: searchInput }]
        fetchData(0, ROWS_PER_PAGE, newFilter)
          .then(r => {
            let searchedOptions = [...options]
            searchedOptions.splice(0, searchedOptions.length, ...r.items)
            searchedOptions = [...removeDuplicates(searchedOptions)]
            setOptions(searchedOptions)
            setRequestFilters(newFilter)
            setIsLastPage(handleIsLastPageCheck(r))
            setPageNumber(1)
          })
          .finally(() => setIsLoading(false))
      }
    }, 200), [fetchData, options, removeDuplicates, searchField]
  )

  function handleIsLastPageCheck (page) {
    return (page.number === page.totalPageCount - 1)
  }

  function fetchInitialOptions (newSelectedOption) {
    fetchData(0, ROWS_PER_PAGE, [])
      .then(response => {
        const initialOptions = [...response.items.filter(i =>
          getOptionValue(i) !== getOptionValue(newSelectedOption))]
        setOptions(initialOptions)
        setIsLastPage(handleIsLastPageCheck(response))
        setRequestFilters([])
        setPageNumber(1)
      })
  }

  return <Autocomplete
    disableClearable={disableClear}
    options={selectedOption ? [selectedOption, ...options] : [...options]}
    getOptionLabel={(option => {
      if (option?.createNewOption) {
        return option.label
      } else {
        return getOptionLabel(option)
      }
    })}
    getOptionSelected={option => option && value && getOptionValue(option) === value}
    filterOptions={(options, params) => {
      const filtered = options
      if (addObjectParams && params.inputValue !== '') {
        filtered.push({
          createNewOption: true,
          inputValue: params.inputValue,
          label: `${t(addObjectParams.addToken)} "${params.inputValue}"`
          ,
        })
      }
      return filtered
    }}
    filterSelectedOptions={false}
    className={className || internalStyles.defaultWidth}
    onChange={(_, newSelectedOption) => {
      if (addObjectParams && newSelectedOption?.createNewOption) {
        addObjectParams.openAddDialog(newSelectedOption.inputValue)
      } else {
        const newValue = newSelectedOption
          ? getOptionValue(newSelectedOption)
          : null
        setSelectedOption(newSelectedOption)
        setValue(newValue)
        onChange(newValue, newSelectedOption)
        fetchInitialOptions(newSelectedOption)
      }
    }}
    value={selectedOption}
    noOptionsText={t('form.select.noOptionsText')}
    popupIcon={disabled ? null : <DropdownIcon/>}
    loading={isLoading}
    onInputChange={onInputChange}
    ListboxProps={{
      style: { maxHeight: 370 },
      onScroll: (event) => {
        const listboxNode = event.currentTarget
        const isScrollBottom = listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight
        if (isScrollBottom && !isLastPage) {
          fetchMoreData()
        }
      }
    }
    }
    renderInput={params => (

      <TextField
        {...params}
        onBlur={(e) => {
          setTouched(true)
        }}
        error={!!error && touched}
        helperText={helperText || <ErrorMessage name={fieldName}/>}
        label={label}
        variant="filled"
        fullWidth
        disabled={disabled}
        required={required === undefined ? isRequired : required}
        className={internalStyles.stylesWithHelperText}
        InputProps={{
          ...params.InputProps,
          className: `${params.InputProps.className} ${inputClassName}`,
          disableUnderline: true,
          endAdornment: (
            <>
              {isLoading && (
                <Loader/>
              )}
              {params.InputProps.endAdornment}
            </>
          )
        }}
      />
    )}
  />
}
